import { useState } from "react";

import { useApi } from "../../shared/api/api-context.js";
import { requireData } from "../../shared/api/api-error.js";
import { Button } from "../../shared/ui/button.js";

const orderIdPattern = /^[0-9A-HJKMNP-TV-Z]{26}$/;

export function PreflightDiagnosticControl() {
  const api = useApi();
  const [orderId, setOrderId] = useState("");
  const [isRunning, setIsRunning] = useState(false);
  const [result, setResult] = useState<unknown>(null);
  const [error, setError] = useState<string | null>(null);

  const run = async () => {
    if (!orderIdPattern.test(orderId) || isRunning) return;
    setIsRunning(true);
    setError(null);
    setResult(null);
    try {
      const diagnostic = requireData(
        await api.POST("/admin/preflight-diagnostics/{orderId}", {
          params: { path: { orderId } },
        }),
      );
      setResult(diagnostic);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Preflight diagnostic failed.");
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <article className="content-card form-stack" aria-labelledby="preflight-diagnostic-title">
      <div>
        <p className="eyebrow">Temporary diagnostic</p>
        <h2 id="preflight-diagnostic-title">Automated Tastytrade preflight</h2>
        <p className="section-description">
          Replays one failed bot order as a broker dry run. It does not submit a live order or
          restart a bot. The backend must allowlist the exact order ID and permits one run only.
        </p>
      </div>
      <label htmlFor="preflight-order-id">Failed bot order ID</label>
      <input
        id="preflight-order-id"
        autoComplete="off"
        onChange={(event) => setOrderId(event.currentTarget.value.trim().toUpperCase())}
        value={orderId}
      />
      <div className="button-row">
        <Button disabled={!orderIdPattern.test(orderId) || isRunning} onClick={() => void run()}>
          {isRunning ? "Running dry run…" : "Run one dry run"}
        </Button>
      </div>
      {error === null ? null : (
        <p className="form-message form-message--error" role="alert">
          {error}
        </p>
      )}
      {result === null ? null : (
        <pre aria-label="Sanitized preflight diagnostic result">
          {JSON.stringify(result, null, 2)}
        </pre>
      )}
    </article>
  );
}
